export const BILLBOARD_SLOT_IDS = ["hourly", "daily", "forever"] as const;

export type BillboardSlotId = (typeof BILLBOARD_SLOT_IDS)[number];

export type BillboardPlacement = {
  id: string;
  message: string;
  destinationUrl: string | null;
  activatedAt: string | null;
};

export type BillboardSlot = {
  id: BillboardSlotId;
  anchorPriceCents: number;
  anchorAt: string;
  currentPriceCents: number;
  version: number;
  placement: BillboardPlacement | null;
};

export type BillboardSlotsResponse = {
  enabled: boolean;
  serverTime: string;
  slots: BillboardSlot[];
};

export type BillboardCreativeInput = {
  slotId: BillboardSlotId;
  message: string;
  destinationUrl?: string;
};

export type BillboardQuote = {
  placementId: string;
  clientSecret: string;
  amountCents: number;
  currency: string;
  slotVersion: number;
  quoteExpiresAt: string;
};

export type BillboardPlacementStatus =
  | "quoted"
  | "reserved"
  | "active"
  | "replaced"
  | "failed"
  | "canceled"
  | "refunded"
  | "disputed"
  | "voided";

export type BillboardStatusResponse = {
  placementId: string;
  slotId: BillboardSlotId;
  status: BillboardPlacementStatus;
  paymentStatus: string | null;
  amountCents: number;
  currency: string;
  quoteExpiresAt: string;
  activatedAt: string | null;
  failureReason: string | null;
};

export type BillboardReportReason =
  "offensive" | "misleading" | "malware" | "other";

export type BillboardReportInput = {
  placementId: string;
  reason: BillboardReportReason;
  details?: string;
};

// The service boundary is intentionally an interface for interchangeable transports.
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface BillboardApi {
  getSlots(): Promise<BillboardSlotsResponse>;
  createPaymentIntent(input: BillboardCreativeInput): Promise<BillboardQuote>;
  getStatus(placementId: string): Promise<BillboardStatusResponse>;
  report(input: BillboardReportInput): Promise<{ accepted: boolean }>;
}
